/**
 * バーコード（JAN = EAN-13 / EAN-8）の純粋関数（SPEC §11）。
 *
 * カメラ・デコーダ・DOM に一切依存しないので単体テストできる。
 * デコーダ側の実装（src/lib/scanner.ts）を差し替えても、
 * 「何を正しいコードとみなすか」の判定はここ 1 箇所に集約される。
 */
import type { ReaderOptions } from 'zxing-wasm/reader';
import type { ItemRow } from '../../shared/types';

/** SPEC §11.3: 対象は EAN-13 / EAN-8（JAN）のみ */
export const SUPPORTED_LENGTHS = [8, 13] as const;

/**
 * zxing-wasm に渡す読み取り設定（SPEC §11.3）。
 *
 * 実装（src/lib/scanner.ts）と統合テスト（scanner.test.ts）で同じ値を使う。
 * ここに型だけ import しているのは、フォーマット名の綴り間違いを
 * コンパイル時に落とすため（実行時の依存は増えない）。
 */
export const EAN_READER_OPTIONS: ReaderOptions = {
  formats: ['EAN-13', 'EAN-8'],
  tryHarder: true,
  // 端末を縦に持ったまま横向きのコードへ寄せることが多いので回転も試す
  tryRotate: true,
  // 商品バーコードが白黒反転していることは無い。1 フレームあたりの時間を削る
  tryInvert: false,
  tryDownscale: true,
  maxNumberOfSymbols: 1,
};

/**
 * 入力を数字だけに正規化する。
 * 全角数字（レシートやメモからのコピペ）・ハイフン・空白を許容する。
 */
export function normalizeBarcode(raw: string): string {
  let out = '';
  for (const ch of raw) {
    const code = ch.codePointAt(0) as number;
    // 半角 0-9
    if (code >= 0x30 && code <= 0x39) out += ch;
    // 全角 ０-９
    else if (code >= 0xff10 && code <= 0xff19) out += String.fromCharCode(code - 0xff10 + 0x30);
  }
  return out;
}

/**
 * モジュラス 10 / ウェイト 3:1 のチェックディジット（EAN-13 / EAN-8 共通）。
 * 重みは末尾（チェックディジットの 1 つ手前）から 3, 1, 3, 1 … と付く。
 */
export function eanCheckDigit(payload: string): number {
  let sum = 0;
  for (let i = 0; i < payload.length; i++) {
    const weight = (payload.length - 1 - i) % 2 === 0 ? 3 : 1;
    sum += Number(payload[i]) * weight;
  }
  return (10 - (sum % 10)) % 10;
}

/** 桁数（8 or 13）とチェックディジットの両方が正しいか */
export function isValidBarcode(code: string): boolean {
  if (!/^\d+$/.test(code)) return false;
  if (!(SUPPORTED_LENGTHS as readonly number[]).includes(code.length)) return false;
  return eanCheckDigit(code.slice(0, -1)) === Number(code[code.length - 1]);
}

/**
 * 手入力・デコード結果を保存できる形にする。正しくなければ null。
 * 保存する値は常に「数字だけの文字列」に揃える（DB の UNIQUE 突き合わせのため）。
 */
export function parseBarcode(raw: string): string | null {
  const normalized = normalizeBarcode(raw);
  return isValidBarcode(normalized) ? normalized : null;
}

/** 表示用の区切り（EAN-13: 1-6-6 / EAN-8: 4-4）。目視確認しやすくするだけ。 */
export function formatBarcode(code: string): string {
  if (code.length === 13) return `${code.slice(0, 1)} ${code.slice(1, 7)} ${code.slice(7)}`;
  if (code.length === 8) return `${code.slice(0, 4)} ${code.slice(4)}`;
  return code;
}

/**
 * バーコードから品目を引く（SPEC §11.2 の「既知バーコード」判定）。
 *
 * items は IndexedDB のキャッシュ（＝有効な品目のみ）を渡す想定。
 * 件数が数十のオーダーなので線形探索で足り、Dexie の索引追加も要らない。
 */
export function findItemByBarcode(items: ItemRow[], code: string): ItemRow | undefined {
  return items.find((i) => i.barcode === code);
}
